import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../lib/utils.ts"
import { Slot } from "radix-ui"

/*
 * shadcn/ui Button, restyled to the design language (components.md, Buttons):
 * one primary per surface (the control fill: one calm single-hue gradient, blue into royal top to
 * bottom, with a white label; hover is the same gradient one step brighter, pressed one step
 * deeper; a 1 px top light and a contact shadow, no coloured glow, never the multicolour ray),
 * secondary on the raised control fill with a --border edge, ghost with a transparent edge,
 * destructive only on the button that performs the destructive action (soft fill, ink text:
 * never white on a status fill). Every variant carries a 1 px edge so labels line up side by
 * side. Heights come from the density tokens: h-control (32 / 28), h-control-sm (28),
 * h-control-xs (24), h-control-lg (40 / 32). Icons are 16 px in md and lg, 14 px in sm and xs.
 * Hover and press are 120 ms colour steps (no scale); focus is the global 2 px --focus outline
 * with a 2 px offset. Disabled keeps the shape, drops the shadow and draws the label in
 * --fg-faint (no opacity: the label never turns to mud); a disabled primary goes neutral,
 * because the accent marks only what you can press.
 */
/** Size classes alone, for merging onto a link styled as a button. */
const buttonSizes = {
  default: "h-control gap-1.5 px-control-x",
  xs: "h-control-xs gap-1 px-2 text-caption [&_svg:not([class*='size-'])]:size-3.5",
  sm: "h-control-sm gap-1.5 px-2 text-caption [&_svg:not([class*='size-'])]:size-3.5",
  lg: "h-control-lg gap-2 px-4",
  icon: "size-control",
  "icon-xs": "size-control-xs [&_svg:not([class*='size-'])]:size-3.5",
  "icon-sm": "size-control-sm [&_svg:not([class*='size-'])]:size-3.5",
  "icon-lg": "size-control-lg",
} as const

const buttonVariants = cva(
  "relative inline-flex shrink-0 select-none items-center justify-center whitespace-nowrap rounded-control border text-ui font-medium transition-colors duration-(--duration-fast) ease-standard disabled:pointer-events-none off:text-fg-faint off:shadow-none aria-invalid:border-status-danger [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary control-fill text-primary-foreground shadow-control-primary hover:control-fill-hover active:control-fill-active active:shadow-none off:border-border off:bg-control off:bg-none",
        destructive:
          "border-transparent bg-destructive-soft text-destructive-ink hover:border-destructive-line active:border-destructive off:bg-control",
        outline:
          "border-border bg-transparent text-fg hover:border-border-strong hover:bg-fg/5 active:bg-fg/10",
        secondary:
          "border-border bg-control text-fg shadow-control hover:border-border-strong hover:bg-control-hover active:bg-control-active active:shadow-none",
        ghost: "border-transparent text-fg-muted hover:bg-fg/6 hover:text-fg active:bg-fg/10",
        link: "h-auto border-0 px-0 text-accent-text underline-offset-4 hover:underline",
      },
      size: buttonSizes,
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonSizes, buttonVariants }
